import {
  CoinsIcon,
  ImageIcon,
  ReceiptTextIcon,
  SendIcon,
  ShieldAlertIcon,
  TicketIcon,
  TrophyIcon,
  UsersIcon,
  UtensilsIcon,
} from 'lucide-react';

import type { AdminStats } from '@qaytim/shared';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/page-header';
import { StatCard } from '@/components/stat-card';
import { StatusBadge } from '@/components/status-badge';
import { ErrorNotice } from '@/components/states';
import { Skeleton } from '@/components/ui/skeleton';
import { DegradedModeBanner } from '@/components/degraded-mode-banner';
import { SystemHealthWidget } from '@/components/system-health-widget';
import { TrendChart, type TrendSeries } from '@/components/trend-chart';
import { formatNumber } from '@/lib/utils';
import { useAdminStats } from '@/lib/admin-queries';
import { formatTashkent } from '@/lib/time';
import { strings } from '@/i18n/strings';

/**
 * Sonlarni bo'shliq bilan ajratish — besh xonali balanslar o'qilishi uchun.
 *
 * `Intl.NumberFormat('uz-UZ')` EMAS: brauzerlarda o'zbek lokalining ma'lumoti to'liq emas va
 * Chrome uni vergulga (`12,480`) tushiradi, holbuki mahsulotning qolgan hamma joyida —
 * Mini App'da ham, ball-katalogda ham — ajratuvchi bo'shliq. Bitta ekranda ikki xil format
 * bo'lgani operatorga "bu boshqa birlikmi" degan savol tug'dirardi.
 */
const n = formatNumber;

const SCAN_SERIES: TrendSeries[] = [
  { key: 'receipts', label: strings.dashboard.seriesReceipts, color: 'var(--chart-1)' },
  { key: 'photos', label: strings.dashboard.seriesPhotos, color: 'var(--chart-2)' },
  { key: 'meals', label: strings.dashboard.seriesMeals, color: 'var(--chart-3)' },
];

const GROWTH_SERIES: TrendSeries[] = [
  { key: 'users', label: strings.dashboard.seriesUsers, color: 'var(--chart-4)' },
  { key: 'tickets', label: strings.dashboard.seriesTickets, color: 'var(--chart-5)' },
];

/**
 * Panelning bosh ekrani.
 *
 * Tartib ataylab teskari: eng tepada O'TMISH emas, KUTIB TURGAN ISH. Operator bu ekranni
 * kunni boshlash uchun ochadi, va uning birinchi savoli "jami nechta foydalanuvchi bor" emas —
 * "hozir nima qilishim kerak". Jami sonlar undan pastda: ular kunlik qarorni o'zgartirmaydi,
 * lekin oyning qanday ketayotganini aytadi.
 *
 * Har bir jami son yonida "bugun" va "shu haftada" turadi. Yalang'och "12 480" har kuni
 * kattalashadigan raqam va u hech qanday qaror qabul qildirmaydi; uning tezligi esa qildiradi.
 */
export function DashboardPage() {
  const stats = useAdminStats();

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        title={strings.dashboard.title}
        description={strings.dashboard.subtitle}
        actions={
          stats.data ? (
            <span className="text-muted-foreground text-xs">
              {strings.dashboard.generatedAt}:{' '}
              <span className="tabular">{formatTashkent(stats.data.generatedAt)}</span>
            </span>
          ) : null
        }
      />

      <DegradedModeBanner />

      {stats.isError ? <ErrorNotice error={stats.error} /> : null}
      {stats.isPending ? <DashboardSkeleton /> : null}

      {stats.data ? <DashboardBody stats={stats.data} /> : null}
    </section>
  );
}

function DashboardBody({ stats }: { stats: AdminStats }) {
  return (
    <>
      <SystemHealthWidget />

      <Card>
        <CardHeader>
          <CardTitle>{strings.dashboard.queuesHeading}</CardTitle>
          <CardDescription>{strings.dashboard.queuesSubtitle}</CardDescription>
        </CardHeader>
        <CardContent className="motion-stagger grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <QueueCard
            label={strings.dashboard.queueFraud}
            value={stats.queues.fraudReview}
            icon={ShieldAlertIcon}
          />
          <QueueCard
            label={strings.dashboard.queuePhotos}
            value={stats.queues.photosInReview}
            icon={ImageIcon}
          />
          <QueueCard
            label={strings.dashboard.queueReceipts}
            value={stats.queues.receiptsPending}
            icon={ReceiptTextIcon}
          />
          <QueueCard
            label={strings.dashboard.queueNotifications}
            value={stats.queues.notificationsFailed}
            icon={SendIcon}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{strings.dashboard.peopleHeading}</CardTitle>
        </CardHeader>
        <CardContent className="motion-stagger grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label={strings.dashboard.statUsers}
            value={n(stats.users.total)}
            icon={UsersIcon}
            hint={`${cadence(stats.users.today, stats.users.week)} · ${n(stats.users.blocked)} ${strings.dashboard.statBlocked}`}
          />
          <StatCard
            label={strings.dashboard.statPointsIssued}
            value={n(stats.points.issued)}
            icon={CoinsIcon}
            hint={`${strings.dashboard.today}: ${n(stats.points.issuedToday)}`}
          />
          <StatCard
            label={strings.dashboard.statPointsReversed}
            value={n(stats.points.reversed)}
            icon={CoinsIcon}
          />
          <StatCard
            label={strings.dashboard.statPointsBalance}
            value={n(stats.points.balance)}
            icon={CoinsIcon}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{strings.dashboard.contentHeading}</CardTitle>
        </CardHeader>
        <CardContent className="motion-stagger grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <StatCard
            label={strings.dashboard.statReceipts}
            value={n(stats.receipts.total)}
            icon={ReceiptTextIcon}
            hint={cadence(stats.receipts.today, stats.receipts.week)}
          />
          <StatCard
            label={strings.dashboard.statPhotos}
            value={n(stats.photos.total)}
            icon={ImageIcon}
            hint={cadence(stats.photos.today, stats.photos.week)}
          />
          <StatCard
            label={strings.dashboard.statMeals}
            value={n(stats.meals.total)}
            icon={UtensilsIcon}
            hint={cadence(stats.meals.today, stats.meals.week)}
          />
          <StatCard
            label={strings.dashboard.statTickets}
            value={n(stats.tickets.total)}
            icon={TicketIcon}
            hint={cadence(stats.tickets.today, stats.tickets.week)}
          />
          <StatCard
            label={strings.dashboard.statDraws}
            value={n(stats.draws.total)}
            icon={TrophyIcon}
          />
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{strings.dashboard.trendScans}</CardTitle>
            <CardDescription>{strings.dashboard.trendSubtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <TrendChart
              points={stats.trend}
              series={SCAN_SERIES}
              label={`${strings.dashboard.trendScans} — ${strings.dashboard.trendHeading}`}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{strings.dashboard.trendGrowth}</CardTitle>
            <CardDescription>{strings.dashboard.trendSubtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            <TrendChart
              points={stats.trend}
              series={GROWTH_SERIES}
              label={`${strings.dashboard.trendGrowth} — ${strings.dashboard.trendHeading}`}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{strings.dashboard.statusHeading}</CardTitle>
        </CardHeader>
        <CardContent className="motion-stagger grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          <StatusBreakdown
            heading={strings.dashboard.statusReceipts}
            counts={stats.receipts.statusCounts}
          />
          <StatusBreakdown
            heading={strings.dashboard.statusTickets}
            counts={stats.tickets.statusCounts}
          />
          <StatusBreakdown
            heading={strings.dashboard.statusPhotos}
            counts={stats.photos.statusCounts}
          />
          <StatusBreakdown
            heading={strings.dashboard.statusMeals}
            counts={stats.meals.statusCounts}
          />
        </CardContent>
      </Card>
    </>
  );
}

/**
 * Navbat kartochkasi.
 *
 * Nolinchi qiymat SO'NIQ chiziladi, qolgani esa ogohlantirish rangida: rangning o'zi "bu yerda
 * ish bor" degan xabarni raqamni o'qishdan oldin yetkazadi.
 */
function QueueCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: typeof ShieldAlertIcon;
}) {
  return (
    <StatCard label={label} value={n(value)} icon={icon} tone={value > 0 ? 'warning' : 'muted'} />
  );
}

/**
 * Holatlar kesimi.
 *
 * Holat qiymatlari TARJIMA QILINMAYDI — moderatsiya jadvallaridagi bilan bir xil qoida: bu
 * ustundagi qiymatning o'zi, va qo'llab-quvvatlash so'rovida operator aynan shu so'zni izlaydi.
 */
function StatusBreakdown({ heading, counts }: { heading: string; counts: Record<string, number> }) {
  const rows = Object.entries(counts).sort(([, a], [, b]) => b - a);
  // Chiziqlar eng katta qiymatga nisbatan o'lchanadi, JAMIGA nisbatan emas: holatlarning
  // birortasi deyarli har doim ustun (masalan "details_ready"), va jamiga nisbatan qolganlari
  // ko'rinmas ipga aylanardi. Aniq son baribir yonida turibdi — chiziq faqat nisbatni aytadi.
  const largest = rows[0]?.[1] ?? 0;

  return (
    <div className="flex flex-col gap-2.5">
      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">{heading}</p>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">{strings.app.empty}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map(([status, count]) => (
            <li key={status} className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <StatusBadge>{status}</StatusBadge>
                <span className="tabular text-sm font-medium">{n(count)}</span>
              </div>
              {/*
                Chiziq ekran o'quvchisidan yashirilgan: u yonidagi sonning aynan o'zini
                takrorlaydi, ya'ni ovozli o'qishda faqat shovqin bo'lardi.
              */}
              <div className="bg-muted h-1 overflow-hidden rounded-full" aria-hidden="true">
                <span
                  className="motion-bar bg-muted-foreground/40 block h-full rounded-full"
                  style={{ width: `${largest === 0 ? 0 : Math.round((count / largest) * 100)}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** "bugun 34 · shu haftada 210" — jami son yonidagi tezlik. */
function cadence(today: number, week: number): string {
  return `${strings.dashboard.today} ${n(today)} · ${strings.dashboard.week} ${n(week)}`;
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((slot) => (
          <Skeleton key={slot} className="h-20 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-72 rounded-xl" />
    </div>
  );
}
